<!--
  Enhanced Table Component
  
  A powerful, fully-featured data table with:
  - Sorting (client-side)
  - Pagination (internal or external)
  - Custom cell rendering via slots
  - Loading states
  - Empty states
  - Dense mode
  - Bordered style
  - Sticky header
  - Clickable rows
  - Striped rows
  - Hover effects
  - Dark mode support
  - Accessibility compliant
  
  Usage:
  <Table
    :data="items"
    :columns="columns"
    :show-pagination="true"
    :page-size="20"
    :clickable="true"
    @row-click="handleRowClick"
  >
    <template #cell-status="{ row, value }">
      <HLBadge id="table-status" color="gray" size="sm">{{ value }}</HLBadge>
    </template>
  </Table>
  
  External Pagination:
  <Table
    :data="items"
    :columns="columns"
    :show-pagination="true"
    :external-pagination="true"
    v-model:current-page="page"
    :total-items="totalCount"
    @page-change="loadPage"
  />
-->
<template>
  <div class="overflow-hidden relative">
    <!-- Loading Overlay -->
    <div v-if="loading" class="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10">
      <HLSpin :spinning="true" size="large">
        <div class="p-4">
          <Text variant="body-sm" class="mt-2">{{ t('common.loading') }}</Text>
        </div>
      </HLSpin>
    </div>
    
    <!-- Table container -->
    <div 
      :class="[
        'overflow-x-auto',
        maxHeight ? 'overflow-y-auto' : '',
        bordered ? 'border border-gray-200 dark:border-gray-700 rounded-lg' : ''
      ]"
      :style="maxHeight ? { maxHeight } : {}"
    >
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <!-- Header -->
        <thead 
          :class="[
            'bg-gray-50 dark:bg-gray-700',
            stickyHeader ? 'sticky top-0 z-10' : ''
          ]"
        >
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="headerClasses(column)"
              @click="handleSort(column)"
            >
              <div :class="column.align === 'center' ? 'flex items-center justify-center' : column.align === 'right' ? 'flex items-center justify-end' : 'flex items-center'">
                <slot
                  :name="`header-${column.key}`"
                  :column="column"
                >
                  <span class="text-gray-900 dark:text-gray-100">{{ column.label }}</span>
                </slot>
                <svg
                  v-if="column.sortable && sortKey === column.key"
                  :class="sortIconClasses"
                  class="ml-2 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    :d="sortDirection === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'"
                  />
                </svg>
              </div>
            </th>
          </tr>
        </thead>
        
        <!-- Body -->
        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          <tr
            v-for="(row, index) in paginatedData"
            :key="getRowKey(row, index)"
            :class="rowClasses(row, index)"
            @click="handleRowClick(row, index)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="cellClasses(column)"
            >
              <slot
                :name="`cell-${column.key}`"
                :row="row"
                :value="getCellValue(row, column)"
                :column="column"
                :index="index"
              >
                {{ getCellValue(row, column) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Empty state -->
    <div v-if="data.length === 0 && !loading" class="py-12">
      <slot name="empty">
        <HLEmpty :description="emptyText || t('table.noData')">
          <template #image>
            <Icon name="document" size="2xl" class="text-gray-400 mx-auto mb-4" />
          </template>
          <template v-if="!emptyText" #description>
            <HLSpace vertical :size="8" align="center">
              <Text variant="body-sm" weight="medium" class="text-gray-900 dark:text-white">
                {{ t('table.noData') }}
              </Text>
              <Text variant="caption" class="text-gray-500 dark:text-gray-400">
                {{ t('table.noDataDescription') }}
              </Text>
            </HLSpace>
          </template>
        </HLEmpty>
      </slot>
    </div>
    
    <!-- Pagination -->
    <div v-if="showPagination && totalPages > 1" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
      <div class="flex items-center justify-between">
        <HLSpace :size="12" class="flex-1 sm:hidden" justify="space-between">
          <HLButton
            :disabled="activePage === 1"
            @click="goToPage(activePage - 1)"
            color="gray"
            variant="secondary"
            size="sm"
          >
            Previous
          </HLButton>
          <HLButton
            :disabled="activePage === totalPages"
            @click="goToPage(activePage + 1)"
            color="gray"
            variant="secondary"
            size="sm"
          >
            Next
          </HLButton>
        </HLSpace>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <Text variant="body-sm">
              Showing
              <span class="font-medium">{{ startItem }}</span>
              to
              <span class="font-medium">{{ endItem }}</span>
              of
              <span class="font-medium">{{ externalPagination ? totalItems : data.length }}</span>
              results
            </Text>
          </div>
          <div>
            <HLPagination
              v-model:current="activePage"
              :total="externalPagination ? totalItems : data.length"
              :page-size="String(pageSize)"
              size="small"
              @update:current="goToPage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { HLSpin, HLEmpty, HLSpace, HLButton, HLPagination } from '@platform-ui/highrise'
import Text from './Text.vue'
import Icon from './Icon.vue'
import { t } from '@/i18n/en-us'

// Props
const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  columns: {
    type: Array,
    required: true
  },
  rowKey: {
    type: [String, Function],
    default: 'id'
  },
  striped: {
    type: Boolean,
    default: false
  },
  hover: {
    type: Boolean,
    default: true
  },
  clickable: {
    type: Boolean,
    default: false
  },
  showPagination: {
    type: Boolean,
    default: false
  },
  pageSize: {
    type: Number,
    default: 20
  },
  sortable: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  dense: {
    type: Boolean,
    default: false
  },
  bordered: {
    type: Boolean,
    default: false
  },
  stickyHeader: {
    type: Boolean,
    default: false
  },
  maxHeight: {
    type: String,
    default: ''
  },
  emptyText: {
    type: String,
    default: ''
  },
  externalPagination: {
    type: Boolean,
    default: false
  },
  currentPage: {
    type: Number,
    default: 1
  },
  totalItems: {
    type: Number,
    default: 0
  }
})

// Emits
const emit = defineEmits(['row-click', 'sort', 'page-change', 'update:currentPage'])

// Reactive data
const internalCurrentPage = ref(1)
const sortKey = ref('')
const sortDirection = ref('asc')

// Use external or internal page
const activePage = computed({
  get: () => props.externalPagination ? props.currentPage : internalCurrentPage.value,
  set: (val) => {
    if (props.externalPagination) {
      emit('update:currentPage', val)
    } else {
      internalCurrentPage.value = val
    }
  }
})

// Computed
const paginatedData = computed(() => {
  if (!props.showPagination || props.externalPagination) {
    return props.data
  }
  
  const start = (activePage.value - 1) * props.pageSize
  const end = start + props.pageSize
  return props.data.slice(start, end)
})

const totalPages = computed(() => {
  const total = props.externalPagination ? props.totalItems : props.data.length
  return Math.ceil(total / props.pageSize)
})

const startItem = computed(() => {
  return (activePage.value - 1) * props.pageSize + 1
})

const endItem = computed(() => {
  const total = props.externalPagination ? props.totalItems : props.data.length
  return Math.min(activePage.value * props.pageSize, total)
})

const visiblePages = computed(() => {
  const pages = []
  const total = totalPages.value
  const current = activePage.value
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    } else if (current >= total - 3) {
      pages.push(1)
      pages.push('...')
      for (let i = total - 4; i <= total; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)
      pages.push('...')
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    }
  }
  
  return pages
})

const sortIconClasses = computed(() => {
  return sortDirection.value === 'asc' ? 'text-indigo-600' : 'text-indigo-600'
})

// Methods
const getRowKey = (row, index) => {
  if (typeof props.rowKey === 'function') {
    return props.rowKey(row, index)
  }
  return row[props.rowKey] || index
}

const getCellValue = (row, column) => {
  if (column.formatter) {
    return column.formatter(row[column.key], row)
  }
  return row[column.key]
}

const headerClasses = (column) => {
  const baseClasses = 'py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
  const paddingClasses = column.key === 'select' ? 'px-2' : 'px-6'
  const alignClasses = column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'
  const sortableClasses = column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''
  return [baseClasses, paddingClasses, alignClasses, sortableClasses].filter(Boolean).join(' ')
}

const rowClasses = (row, index) => {
  const baseClasses = 'hover:bg-gray-50 dark:hover:bg-gray-700'
  const stripedClasses = props.striped && index % 2 === 1 ? 'bg-gray-50 dark:bg-gray-700' : ''
  const hoverClasses = props.hover ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : ''
  const clickableClasses = props.clickable ? 'cursor-pointer' : ''
  return [baseClasses, stripedClasses, hoverClasses, clickableClasses].filter(Boolean).join(' ')
}

const cellClasses = (column) => {
  const baseClasses = 'whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100 max-w-xs'
  const paddingVertical = props.dense ? 'py-2' : 'py-3'
  const paddingHorizontal = column.key === 'select' ? 'pl-2' : (props.dense ? 'px-4' : 'px-6')
  const alignClasses = column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left'
  return [baseClasses, paddingVertical, paddingHorizontal, alignClasses].join(' ')
}

const pageButtonClasses = (page) => {
  const baseClasses = 'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
  const activeClasses = page === activePage.value
    ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-600 text-indigo-600 dark:text-indigo-400'
    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
  return [baseClasses, activeClasses].join(' ')
}

const handleSort = (column) => {
  if (!column.sortable) return
  
  if (sortKey.value === column.key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = column.key
    sortDirection.value = 'asc'
  }
  
  emit('sort', { key: column.key, direction: sortDirection.value })
}

const handleRowClick = (row, index) => {
  if (props.clickable) {
    emit('row-click', row, index)
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    activePage.value = page
    emit('page-change', page)
  }
}

// Watch for data changes
watch(() => props.data, () => {
  if (!props.externalPagination) {
    internalCurrentPage.value = 1
  }
})
</script>